<template>

    <div class="relative flex flex-col w-full h-full canvas::editor-admin-Gallery">

        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200.26 68.42"
             class="absolute w-40 -mr-4 pointer-events-none arrow"
             v-if="canvasVersion.canvas_blocks.length == 0">
            <path d="M6.19,9.4Q4.9,7.87,3.62,6.3L5.18,5Q6.44,6.6,7.72,8.11Z"></path>
            <path
                d="M139.15,68.42q-2,0-4,0l0-2q4.09.08,8.16,0l0,2Q141.29,68.42,139.15,68.42ZM128,68.13c-2.75-.15-5.52-.35-8.22-.61l.19-2c2.68.25,5.42.45,8.14.6Zm22.65,0-.11-2c2.7-.14,5.44-.33,8.15-.58l.18,2C156.12,67.78,153.36,68,150.63,68.12ZM166,66.79l-.24-2c2.69-.32,5.41-.7,8.09-1.11l.31,2C171.48,66.09,168.73,66.47,166,66.79Zm-53.43-.08c-2.73-.36-5.47-.78-8.15-1.25l.35-2c2.64.47,5.36.88,8.06,1.24Zm68.69-2.25-.37-2c2.66-.49,5.35-1,8-1.62l.43,2C186.68,63.41,184,64,181.28,64.46Zm-83.92-.4c-2.68-.58-5.38-1.24-8-1.94l.52-1.93c2.6.69,5.27,1.34,7.93,1.92Zm-15-3.94c-2.63-.82-5.26-1.71-7.81-2.65l.69-1.88c2.53.93,5.12,1.81,7.72,2.62ZM67.87,54.84c-2.54-1.06-5.07-2.19-7.53-3.37l.86-1.8C63.63,50.83,66.13,52,68.64,53Zm-14-6.62c-2.41-1.29-4.82-2.66-7.17-4.07l1-1.71c2.32,1.4,4.71,2.75,7.08,4ZM40.62,40.27c-2.28-1.52-4.55-3.12-6.74-4.75l1.19-1.6c2.17,1.62,4.41,3.19,6.66,4.69ZM28.19,31.08c-2.11-1.73-4.22-3.54-6.25-5.38l1.34-1.48c2,1.82,4.09,3.61,6.18,5.31ZM16.69,20.75c-1.94-1.91-3.86-3.91-5.73-5.93l1.47-1.35c1.84,2,3.74,4,5.66,5.86Z"></path>
            <path d="M196.38,61.16l-.49-1.94q1.94-.49,3.85-1l.52,1.93Q198.33,60.67,196.38,61.16Z"></path>
            <polygon points="1.39 9.88 0 0 9.24 3.73 1.39 9.88"></polygon>
        </svg>

        <div class="h-full pt-4 pl-5 overflow-auto">

            <div class="pr-4 mb-3">
                <h4 class="my-0">Blok toevoegen</h4>
                <h6 class="mt-1 mb-0">Kies een blok en sleep deze links op de pagina om toe te voegen.</h6>
            </div>

            <!-- Filter bar: groups left, tags right -->
            <div class="sticky top-0 z-10 flex items-center gap-3 px-1 py-3 mr-5 mb-2 bg-white border-b border-gray-100">

                <!-- Group filters (left) -->
                <div class="relative z-0 inline-flex flex-wrap rounded-md shadow-sm">
                    <button type="button"
                        class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium border border-gray-300 rounded-l-md focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        :class="!activeGroupFilter
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-white text-gray-700 hover:bg-gray-50'"
                        @click="activeGroupFilter = null">Alles</button>
                    <button v-for="(category, key) in activeGalleryData"
                        :key="'grp-' + category.id"
                        type="button"
                        class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium border border-gray-300 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        :class="[
                            activeGroupFilter === category.id ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50',
                            key === activeGalleryData.length - 1 ? 'rounded-r-md' : ''
                        ]"
                        @click="activeGroupFilter = activeGroupFilter === category.id ? null : category.id">
                        {{ category.label }}
                    </button>
                </div>

                <!-- Tag filters (right) -->
                <div class="relative z-0 inline-flex flex-wrap ml-auto rounded-md shadow-sm">
                    <button v-for="(tag, key) in tagKeys"
                        :key="'tag-' + tag.key"
                        type="button"
                        class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium border border-gray-300 focus:z-10 focus:outline-none transition-colors"
                        :class="[
                            activeTagFilters.includes(tag.key)
                                ? tag.color + ' border-gray-300 text-black'
                                : 'bg-white text-gray-500 hover:bg-gray-50',
                            key === 0 ? 'rounded-l-md' : '',
                            key === tagKeys.length - 1 ? 'rounded-r-md' : ''
                        ]"
                        @click="toggleTagFilter(tag.key)">
                        {{ tag.label }}
                    </button>
                </div>
            </div>

            <div class="pr-5">

                <!-- Single grid — section/subsection headers span full row to act as visual dividers without breaking grid flow -->
                <div class="grid gap-6 pb-4 md:grid-cols-2 lg:grid-cols-3 blocks">

                    <template v-for="(item, idx) in galleryItems">

                        <!-- Section header -->
                        <div v-if="item.type === 'section-header'"
                             :key="'sh-' + item.name"
                             class="col-span-full flex items-center gap-2 px-2 py-2"
                             :class="{ 'mt-2': idx > 0 }">
                            <div class="h-px flex-1 bg-gray-200"></div>
                            <span class="text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                {{ item.label === 'undefined' ? 'Standaard' : item.label }}
                            </span>
                            <div class="h-px flex-1 bg-gray-200"></div>
                        </div>

                        <!-- Subsection header -->
                        <div v-else-if="item.type === 'subsection-header'"
                             :key="'sub-' + item.id"
                             class="col-span-full flex items-center gap-2 px-2"
                             :class="{ 'mt-2': item.notFirst }">
                            <span class="text-[11px] font-medium text-gray-400 italic">{{ item.label }}</span>
                            <div class="h-px flex-1 bg-gray-100"></div>
                        </div>

                        <!-- Block tile -->
                        <div v-else
                            :key="'block-' + item.block.id"
                            class="flex transition-all hover:opacity-50"
                            :class="{
                                'is-excluded-regular': item.block.meta && item.block.meta.excluded && !isSuperadmin,
                                'is-excluded-superadmin': item.block.meta && item.block.meta.excluded && isSuperadmin,
                            }"
                        >

                            <button
                                type="button"
                                class="flex flex-col w-full h-auto col-span-1 text-center bg-white divide-y divide-gray-200 rounded-lg shadow md:h-80"
                                :draggable="isBlockUsable(item.block) ? 'true' : 'false'"
                                :title="item.block.meta && item.block.meta.excluded ? (item.block.meta.excludedReason || 'Not available in this context') : item.block.id"
                                :disabled="!isBlockUsable(item.block)"
                                @dragstart="onDragStart(item.block, $event)"
                                @dragend="onDragEnd"
                                @click="addBlock(item.block)"
                                @contextmenu.prevent="onContextMenu(item.block, $event)"
                            >

                                <div class="flex flex-col flex-1 w-full px-2 py-5 mx-auto">

                                    <div v-if="item.block.icon ? item.block.icon.original : false"
                                         class="flex items-center h-32">
                                        <img :src="convertSvgToImageSrc(item.block.icon.original)"
                                             draggable="false"
                                             :title="item.block.id"
                                             class="h-full mx-auto"/>
                                    </div>

                                    <div v-else-if="item.block.icon"
                                         class="flex items-center h-32">
                                        <img :src="item.block.icon"
                                             draggable="false"
                                             :title="item.block.id"/>
                                    </div>

                                    <div v-else-if="item.block.meta.icon"
                                         class="relative flex items-center h-32 icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="457.361" height="457.361" viewBox="0 0 457.361 457.361">
                                            <g transform="translate(-1781 -3305)">
                                                <path d="M371.605,457.361H85.755A85.75,85.75,0,0,1,0,371.605V85.755A85.75,85.75,0,0,1,85.755,0h285.85a85.75,85.75,0,0,1,85.755,85.755v285.85A85.75,85.75,0,0,1,371.605,457.361Z" transform="translate(1781 3305)" fill="#d8d8d8"/>
                                            </g>
                                        </svg>
                                        <i :class="item.block.meta.icon" class="absolute left-0 right-0 text-4xl" />
                                    </div>

                                    <div v-else
                                         class="flex items-center h-32">
                                        <img src="/vendor/galaxy/canvas/img/editor/admin/question-mark.svg"
                                             draggable="false"
                                             :title="item.block.id"
                                             class="h-full mx-auto"/>
                                    </div>

                                    <h3 class="w-full mx-auto mt-6 text-base font-medium text-gray-900 md:truncate"
                                        :title="item.block.meta.name">{{ item.block.meta.name }}</h3>

                                    <dl class="flex flex-col justify-between flex-grow mt-1">
                                        <dt class="sr-only">Description</dt>
                                        <dd class="mx-6 text-sm text-gray-400 description"
                                            :title="item.block.meta.description">{{ item.block.meta.description }}
                                        </dd>
                                        <dt class="sr-only">Role</dt>
                                        <dd class="mt-3">
                                            <span v-for="(color, text) in item.block.meta.tags"
                                                  :class="color"
                                                  class="px-2 py-1 mx-1 text-xs font-medium text-black rounded-full">
                                                {{ text }}
                                            </span>
                                        </dd>
                                    </dl>
                                </div>

                            </button>

                        </div>

                    </template>

                </div>

                <!-- No results -->
                <div v-if="mergedSections.length === 0" class="py-10 text-center">
                    <p class="text-sm text-gray-500">Geen blokken gevonden met deze filters.</p>
                    <button type="button"
                        class="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
                        @click="activeGroupFilter = null; activeTagFilters = []">
                        Filters wissen
                    </button>
                </div>

            </div>

        </div>

    </div>

</template>

<script>

export default {

    props: {
        canvasVersion: Object,
        canvasBlocksGalleryData: Array,
        canvasId: String,
        isSuperadmin: { type: Boolean, default: false },
    },

    data()
    {
        return {
            activeGroupFilter: 'common',
            activeTagFilters: [],
            groupBeforeTagSwitch: null, // Remembers group before auto-switching to "Alles"
        };
    },

    methods: {

        onDragStart(block, event)
        {
            // Block drag for unusable blocks (excluded + non-superadmin).
            if (!this.isBlockUsable(block)) {
                event?.preventDefault?.();
                return;
            }
            this.$emit('onDraggingBlock', block);
        },

        onDragEnd()
        {
            this.$emit('onDraggingBlock', null);
        },

        addBlock(block)
        {
            // Block add for unusable blocks.
            if (!this.isBlockUsable(block)) {
                return;
            }
            this.$parent.$refs['canvasComponent'].$refs['iframe'].iframeApp.pageComponent.addBlock(block);
        },

        // @ugly and slow?
        convertSvgToImageSrc(svg)
        {
            return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent((svg))));
        },

        /**
         * Whether the block appears in the gallery at all. Regular admins don't
         * see context-excluded blocks. Superadmins see everything (muted) so they
         * can still add blocks and access the right-click menu to manage rules.
         */
        showBlock(canvasBlock)
        {
            if (!canvasBlock) return false;

            const active = canvasBlock.meta?.active ?? true;
            if (!active) return false;

            // Hide context-excluded blocks for regular admins.
            if (canvasBlock.meta?.excluded && !this.isSuperadmin) {
                return false;
            }

            return true;
        },

        /**
         * Whether the block can actually be dragged / added. Same logic as
         * showBlock for now — kept separate so we can later add "show but
         * disabled with tooltip" behavior for regular users.
         */
        isBlockUsable(canvasBlock)
        {
            if (!canvasBlock) return false;
            if (canvasBlock.meta?.excluded && !this.isSuperadmin) {
                return false;
            }
            return true;
        },

        toggleTagFilter(tagKey)
        {
            const idx = this.activeTagFilters.indexOf(tagKey);
            if (idx >= 0) {
                // Deselecting — restore previous group if we auto-switched.
                this.activeTagFilters.splice(idx, 1);

                if (this.activeTagFilters.length === 0 && this.groupBeforeTagSwitch !== null) {
                    this.activeGroupFilter = this.groupBeforeTagSwitch;
                    this.groupBeforeTagSwitch = null;
                }
            } else {
                // Selecting a tag.
                this.activeTagFilters.push(tagKey);

                // If the current group has no blocks matching, auto-switch
                // to "Alles" and remember where we were.
                if (this.activeGroupFilter) {
                    this.$nextTick(() => {
                        if (this.mergedSections.length === 0) {
                            this.groupBeforeTagSwitch = this.groupBeforeTagSwitch ?? this.activeGroupFilter;
                            this.activeGroupFilter = null;
                        }
                    });
                }
            }
        },


        /**
         * Groups blocks by consecutive meta.subsection values.
         * Blocks with the same subsection next to each other are grouped.
         */
        getSubsections(blocks)
        {
            const visible = blocks.filter(b => this.showBlock(b));
            if (!visible.length) return [{ label: null, blocks: visible }];

            const groups = [];
            let currentLabel = undefined;

            for (const block of visible) {
                const sub = block.meta?.subsection || null;
                if (sub !== currentLabel || groups.length === 0) {
                    groups.push({ label: sub, blocks: [] });
                    currentLabel = sub;
                }
                groups[groups.length - 1].blocks.push(block);
            }

            return groups;
        },

        passesTagFilter(block)
        {
            if (!block) return false;
            if (this.activeTagFilters.length === 0) return true;
            const blockTagLabels = Object.keys(block.meta?.tags || {});
            if (blockTagLabels.length === 0) return true;
            return blockTagLabels.some(label =>
                this.activeTagFilters.some(key =>
                    this.allTagKeys.find(t => t.key === key)?.label === label
                )
            );
        },

        onContextMenu(block, event)
        {
            // Only superadmins can manage exclusions; fall through to native
            // context menu for everyone else.
            if (!this.isSuperadmin) return;

            this.$emit('showBlockContextMenu', {
                block,
                x: event.clientX,
                y: event.clientY,
            });
        },

    },

    computed: {

        activeGalleryData()
        {
            const order = ['common', 'sky', 'modules', 'custom'];
            return this.canvasBlocksGalleryData
                .filter(category => category.id != 'utils')
                .sort((a, b) => {
                    const ai = order.indexOf(a.id);
                    const bi = order.indexOf(b.id);
                    return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                });
        },

        /**
         * All blocks from filtered categories, merged and grouped by section.
         * When a group filter is active, only blocks from that category are
         * included. Sections are shared across categories — so "Afbeelding &
         * tekst" blocks from both Algemeen and Sky appear in one section.
         */
        mergedSections()
        {
            let categories = this.activeGalleryData;

            if (this.activeGroupFilter) {
                categories = categories.filter(c => c.id === this.activeGroupFilter);
            }

            // Merge all blocks from all visible categories.
            const allBlocks = categories.flatMap(c => c.blocks || [])
                .filter(b => this.showBlock(b) && this.passesTagFilter(b));

            // Group by section.
            const grouped = _.groupBy(allBlocks, b => b.meta?.section || 'Standaard');
            let sections = Object.keys(grouped).map(name => ({ name, blocks: grouped[name] }));

            // Sort sections by configured order.
            const sectionOrder = window.canvasSectionOrder || [
                'Tekst & kolommen',
                'Afbeelding & tekst',
                'Cards & overzichten',
                'Media & sliders',
                'Formulier & CTA',
                'Header',
                'Footer',
            ];
            if (sectionOrder.length > 0) {
                sections.sort((a, b) => {
                    const ai = sectionOrder.indexOf(a.name);
                    const bi = sectionOrder.indexOf(b.name);
                    const ra = ai === -1 ? sectionOrder.length : ai;
                    const rb = bi === -1 ? sectionOrder.length : bi;
                    // Configured sections by position; any section not in the
                    // order shares the trailing rank — break that tie
                    // alphabetically so the tail is deterministic, not arbitrary.
                    if (ra !== rb) return ra - rb;
                    return a.name.localeCompare(b.name);
                });
            }

            // Sort blocks within each section.
            const activeFilters = this.activeTagFilters;
            const tagDefs = this.tagKeys;
            sections.forEach(s => {
                s.blocks.sort((a, b) => {
                    // When tag filters are active, prioritize blocks that ONLY
                    // match the filtered tags (exact match) over blocks with
                    // additional tags.
                    if (activeFilters.length > 0) {
                        const aLabels = Object.keys(a.meta?.tags || {});
                        const bLabels = Object.keys(b.meta?.tags || {});
                        const filterLabels = tagDefs.filter(t => activeFilters.includes(t.key)).map(t => t.label);
                        const aExtra = aLabels.filter(l => !filterLabels.includes(l)).length;
                        const bExtra = bLabels.filter(l => !filterLabels.includes(l)).length;
                        if (aExtra !== bExtra) return aExtra - bExtra;
                    }
                    // Coerce a non-numeric / missing sort to 99 (end of section),
                    // then break ties by name so equal-sort blocks stay stable.
                    const sa = Number.isFinite(+a.meta?.sort) ? +a.meta.sort : 99;
                    const sb = Number.isFinite(+b.meta?.sort) ? +b.meta.sort : 99;
                    if (sa !== sb) return sa - sb;
                    return (a.meta?.name || a.id || '').localeCompare(b.meta?.name || b.id || '');
                });
            });

            return sections.filter(s => s.blocks.length > 0);
        },

        /**
         * Hide section/subsection headers when the visible block count is small.
         * Sort order is still applied — just no visual dividers/labels —
         * so the gallery doesn't waste space on tiny groups.
         */
        showSectionHeaders()
        {
            const totalBlocks = this.mergedSections.reduce((sum, s) => sum + s.blocks.length, 0);
            return totalBlocks > 9;
        },

        /**
         * Flattens sections + subsections into a single ordered list of items.
         * Each item is either a header (section/subsection) or a block.
         * When showSectionHeaders is false, only blocks are returned — they
         * still flow in section/subsection order so similar blocks sit together.
         */
        galleryItems()
        {
            const items = [];
            const showHeaders = this.showSectionHeaders;

            for (const section of this.mergedSections) {
                if (showHeaders) {
                    items.push({ type: 'section-header', name: section.name, label: section.name });
                }

                const subsections = this.getSubsections(section.blocks);
                let subIdx = 0;

                for (const sub of subsections) {
                    if (showHeaders && sub.label) {
                        items.push({
                            type: 'subsection-header',
                            id: section.name + '-' + sub.label,
                            label: sub.label,
                            notFirst: subIdx > 0,
                        });
                    }

                    for (const block of sub.blocks) {
                        items.push({ type: 'block', block });
                    }

                    subIdx++;
                }
            }

            return items;
        },

        allTagKeys()
        {
            return window.canvasTagDefinitions || [
                { key: 'text', label: 'Tekst', color: 'bg-green-100' },
                { key: 'image', label: 'Afbeelding', color: 'bg-blue-100' },
                { key: 'video', label: 'Video', color: 'bg-yellow-100' },
                { key: 'slider', label: 'Slider', color: 'bg-pink-100' },
                { key: 'module', label: 'Module', color: 'bg-purple-100' },
                { key: 'widget', label: 'Widget', color: 'bg-orange-100' },
            ];
        },

        /**
         * Show tag filters based on ALL blocks across all groups — not
         * limited by the active group filter.
         */
        tagKeys()
        {
            const allLabels = new Set();
            for (const cat of this.activeGalleryData) {
                for (const block of (cat.blocks || [])) {
                    if (!this.showBlock(block)) continue;
                    for (const label of Object.keys(block.meta?.tags || {})) {
                        allLabels.add(label);
                    }
                }
            }

            return this.allTagKeys.filter(tag => allLabels.has(tag.label));
        },

    },

}

</script>

<style lang="scss">
.canvas\:\:editor-admin-Gallery {
    .blocks {
        button {
            svg {
                @apply h-full w-auto mx-auto;
            }
        }
    }
}
</style>

<style lang="scss" scoped>

.canvas\:\:editor-admin-Gallery {
    border-left: 1px solid #e2e8f0;

    .arrow {
        fill: #909090;
        right: calc(100% - 25px);
        top: 185px;
    }

    h6 {
        color: #90a4ae;
        font-weight: 300;
    }

    // Excluded block states
    .is-excluded-superadmin {
        opacity: 0.55;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(239, 68, 68, 0.08) 10px,
                rgba(239, 68, 68, 0.08) 20px
            );
            pointer-events: none;
            border-radius: 0.5rem;
        }
    }

    .is-excluded-regular {
        opacity: 0.35;
        cursor: not-allowed;

        button {
            pointer-events: none;
        }
    }

    .blocks {

        button {
            transition: box-shadow .3s;

            &:active {
                border: 1px solid #edf2f7 !important;
            }

            &:hover {
                @apply shadow;
            }

            img {
                max-width: 100%;
                user-select: none;
                max-height: 100%;
                margin: auto;
            }

            .description {
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .icon i {
                color: #646464;
            }
        }
    }

}

</style>
